/**
 * The mark on a round-type tile: a snowflake for FROST, a flame for FIRE.
 *
 * A mark and not a word, because the board carries no text on any tile (B20,
 * owner sign-off) — the two headers are the only copy on it.
 *
 * Both are drawn to survive being ~22 px tall on a stream, which rules out fine
 * detail and rewards a strong outline. The flake is six spokes on a hexagonal
 * hub with two pairs of branches each, so it stays a flake rather than becoming
 * an asterisk when it shrinks; the flame is one body with a lighter core, so it
 * reads as a flame and not a leaf.
 *
 * The idle motion lives in the stylesheet, on `__svg`, and is transform-only —
 * the flake turns, the flame flickers — so neither reflows anything and both
 * stop when the tile locks.
 *
 * Decorative: the tile's `birdie-sr-only` name is what a screen reader reads.
 */
export type RoundMarkKind = 'FROST' | 'FIRE'

interface Props {
  kind: RoundMarkKind
  /** Box size in REFERENCE px, scaled by the board's transform like everything else. */
  size: number
}

export function RoundMark({ kind, size }: Props) {
  const style = { width: `${size}px`, height: `${size}px` }
  return (
    <span aria-hidden="true" className={`birdie-mark birdie-mark--${kind.toLowerCase()}`} style={style}>
      <svg className="birdie-mark__svg" viewBox="0 0 32 32" fill="none" focusable="false">
        {kind === 'FROST' ? (
          <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
            <g strokeWidth="2">
              {/* three spokes through the hub — six arms */}
              <path d="M16 3.4v25.2M5.09 9.7l21.82 12.6M5.09 22.3 26.91 9.7" />
            </g>
            <g strokeWidth="1.7">
              {/* outer branches, two thirds along each arm */}
              <path d="m16 8.6-3.1-3.1M16 8.6l3.1-3.1M16 23.4l-3.1 3.1M16 23.4l3.1 3.1" />
              <path d="m9.6 12.3-4.24-.5M9.6 12.3l-.6-4.23M22.4 19.7l4.24.5M22.4 19.7l.6 4.23" />
              <path d="m9.6 19.7-4.24.5M9.6 19.7l-.6 4.23M22.4 12.3l4.24-.5M22.4 12.3l.6-4.23" />
            </g>
            <g strokeWidth="1.4" opacity="0.85">
              {/* inner branches, close to the hub */}
              <path d="m16 13.1-2.1-2.1M16 13.1l2.1-2.1M16 18.9l-2.1 2.1M16 18.9l2.1 2.1" />
            </g>
            {/* the hub: what keeps it a crystal rather than a star */}
            <path
              d="m16 12.6 2.95 1.7v3.4L16 19.4l-2.95-1.7v-3.4Z"
              strokeWidth="1.6"
              fill="currentColor"
              fillOpacity="0.28"
            />
          </g>
        ) : (
          <g>
            {/* One clean tongue. The earlier shape had a thin leaning spike at the shoulder that
                detached from the body at tile size and read as a hook rather than a flame. */}
            <path
              fill="currentColor"
              d="M16 2.3c4.7 5.7 7.1 9.5 7.1 13.5a7.1 7.1 0 1 1-14.2 0c0-2.6 1.1-4.8 2.7-6.8.1 2 .8 3.3 2 4-1-4.2-.3-7.6 2.4-10.7Z"
            />
            {/* the core is BRIGHTER, not darker. On a dark tile a dark centre reads as a hole
                punched through the flame; heat reads as a pale heart low in the body. */}
            <ellipse className="birdie-mark__core" cx="16" cy="21.4" rx="2.5" ry="3.4" />
          </g>
        )}
      </svg>
    </span>
  )
}
